import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";

/** What this address is (GET /site). */
export type SiteInfo = {
  /** single: the original one-business setup (production today); multi: the Invora platform. */
  mode: "single" | "multi";
  /** landing page, admin dashboard, a business, or an unregistered/suspended address. */
  kind: "landing" | "admin" | "tenant" | "unavailable";
  platform: { name: string; domain: string };
  tenant: { slug: string; name: string; logoUrl: string | null; brandColor: string | null } | null;
};

/** Root of the site query key; kept when sign-in/out clears user data. */
export const SITE_QUERY_ROOT = "site";
export const siteQueryKey = [SITE_QUERY_ROOT] as const;

/** Branding of the single-business setup (Care and Clean, production). */
export const DEFAULT_BUSINESS_NAME = "Care and Clean";
export const DEFAULT_LOGO_URL = (import.meta.env.VITE_BRAND_LOGO_URL as string | undefined) || "/logo.avif";

/** What an API without /site (deployed before this version) means: the single-business setup. */
const SINGLE_BUSINESS_SITE: SiteInfo = { mode: "single", kind: "tenant", platform: { name: "Invora", domain: "invora.digital" }, tenant: null };

export function useSiteQuery() {
  return useQuery({
    queryKey: siteQueryKey,
    queryFn: async ({ signal }) => {
      try {
        return await api.get<SiteInfo>("/site", undefined, signal);
      } catch (error) {
        // The frontend and API deploy separately (Vercel / Render); never lock staff out meanwhile.
        if (error instanceof ApiError && error.status === 404) return SINGLE_BUSINESS_SITE;
        throw error;
      }
    },
    staleTime: Infinity,
  });
}

/** Name and logo of the business this app belongs to. A business without a logo shows its name. */
export function useBusiness() {
  const { data } = useSiteQuery();
  const tenant = data?.mode === "multi" ? data.tenant : null;
  return {
    ready: !!data,
    multi: data?.mode === "multi",
    platformName: data?.platform.name ?? "Invora",
    name: tenant?.name ?? DEFAULT_BUSINESS_NAME,
    logoUrl: data?.mode === "multi" ? (tenant?.logoUrl ?? null) : DEFAULT_LOGO_URL,
  };
}

/**
 * Address of a business on the current platform host, e.g. from
 * admin.localhost:5173 -> http://sibea.localhost:5173.
 */
export function businessUrl(slug: string) {
  const { protocol, hostname, port } = window.location;
  const base = hostname.replace(/^(admin|www)\./, "");
  return `${protocol}//${slug}.${base}${port ? `:${port}` : ""}`;
}

/** The platform's own home page (landing), from any subdomain. */
export function platformHomeUrl() {
  const { protocol, hostname, port } = window.location;
  const parts = hostname.split(".");
  const base = parts.length > 1 ? parts.slice(1).join(".") : hostname;
  return `${protocol}//${base}${port ? `:${port}` : ""}`;
}
